import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Toolbar,
  Chip,
  Avatar,
  Paper,
  InputAdornment,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  FactCheck,
  Redeem,
  ShoppingBag,
  Cancel,
  Group,
  Search,
  Storefront,
  CalendarMonth,
  Payments,
} from '@mui/icons-material';
import axios from 'axios';
import { API } from '../../utils/Api';

const CustomerSupport = () => {
  const token = localStorage.getItem('token');

  const [mobileNumber, setMobileNumber] = useState('');
  const [customerData, setCustomerData] = useState(null);
  const [error, setError] = useState('');
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState('ALL');

  const cardSx = {
    borderRadius: 3,
    border: '1px solid #edf0f2',
    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.05)',
    height: '100%',
  };

  const sectionTitleSx = {
    fontSize: { xs: 15, sm: 16 },
    fontWeight: 800,
    color: '#0f172a',
  };

  const labelSx = {
    fontSize: 11,
    color: 'text.secondary',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };

  const valueSx = {
    fontSize: 13,
    fontWeight: 700,
    color: '#111827',
    wordBreak: 'break-word',
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
      case 'DELIVERED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      case 'CONFIRMED':
        return 'primary';
      case 'READY-TO-PICK':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPaymentChipColor = (status) => {
    if (status === 'captured') return 'success';
    if (status === 'refunded') return 'warning';
    if (status === 'failed') return 'error';
    if (status === 'created') return 'info';
    return 'default';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mobileNumber.length !== 10 || isNaN(mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    setError('');
    setApiError('');
    setLoading(true);
    setCustomerData(null);

    try {
      const response = await axios.get(
        `${API.BASE_URL}user/getCustomerDetails?mobile=${mobileNumber}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCustomerData(response?.data);
    } catch (err) {
      setApiError(
        err?.response?.data?.data?.message ||
          err?.response?.data?.message ||
          'Unable to fetch customer details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const orders = customerData?.orderStats?.orders || [];
  const coupons = customerData?.userAllocatedCoupons || [];
  const referrals = customerData?.referrals?.referredUsers || [];
  const joiningDate = customerData?.customerDetails?.joiningDate;
  const birthDate = customerData?.customerDetails?.dob;
  const cartData = customerData?.cartData?.cart || null;
  const cartItems = customerData?.cartData?.cartItems || [];
  const cartCoupon = cartData?.coupon || null;

  const getAddonTotal = (addons = []) =>
    addons.reduce((total, addon) => total + Number(addon?.price || 0), 0);

  const isFreebieItem = (item) =>
    item?.product?.isFreebieProduct || Number(item?.grandTotal || 0) === 0;

  const joiningCouponExpired =
    joiningDate &&
    !customerData?.couponDetails?.joiningOfferUsed &&
    new Date() >
      new Date(
        new Date(joiningDate).setDate(new Date(joiningDate).getDate() + 30)
      );

  const filteredOrders = useMemo(() => {
    if (orderFilter === 'ALL') return orders;
    return orders.filter((order) => order.status === orderFilter);
  }, [orders, orderFilter]);

  const recentOrders = filteredOrders.slice(0, 15);

  const birthdayCouponStatus = useMemo(() => {
    if (!birthDate) {
      return {
        label: 'Birthday not added',
        color: 'default',
        birthDateText: '-',
      };
    }

    const today = new Date();
    const bday = new Date(birthDate);
    const isBirthdayMonth = today.getMonth() === bday.getMonth();

    if (customerData?.couponDetails?.birthdayCouponUsage) {
      return {
        label: 'Used',
        color: 'success',
        birthDateText: formatDate(birthDate),
      };
    }

    return {
      label: isBirthdayMonth ? 'Available' : 'Not Available',
      color: isBirthdayMonth ? 'warning' : 'default',
      birthDateText: formatDate(birthDate),
    };
  }, [birthDate, customerData?.couponDetails?.birthdayCouponUsage]);

  const summaryCards = [
    {
      label: 'Total Orders',
      value: customerData?.orderStats?.totalOrders || 0,
      icon: <ShoppingBag fontSize="small" />,
      color: '#1976d2',
      bg: '#e3f2fd',
    },
    {
      label: 'Cancelled',
      value: customerData?.orderStats?.canceledOrders || 0,
      icon: <Cancel fontSize="small" />,
      color: '#d32f2f',
      bg: '#ffebee',
    },
    {
      label: 'Referrals',
      value: customerData?.referrals?.referredCount || 0,
      icon: <Group fontSize="small" />,
      color: '#388e3c',
      bg: '#e8f5e9',
    },
    {
      label: 'Coupons',
      value: coupons.length,
      icon: <Redeem fontSize="small" />,
      color: '#f57c00',
      bg: '#fff3e0',
    },
  ];

  const InfoItem = ({ label, value }) => (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography sx={valueSx}>{value || '-'}</Typography>
    </Box>
  );

  const EmptyText = ({ children }) => (
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
      {children}
    </Typography>
  );

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        bgcolor: '#f6f8fb',
        minHeight: '100vh',
      }}
    >
      <Toolbar />

      <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mt: { xs: 1, md: 2 }, mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: { xs: 22, sm: 26, md: 30 },
              fontWeight: 900,
              color: '#003049',
              lineHeight: 1.15,
            }}
          >
            Customer Support Dashboard
          </Typography>

          <Typography
            sx={{
              mt: 0.8,
              fontSize: { xs: 12.5, sm: 13.5 },
              color: 'text.secondary',
            }}
          >
            Search customer by mobile number and view complete support details.
          </Typography>
        </Box>

        <Paper
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 2.5,
            display: 'flex',
            gap: 1.5,
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            borderRadius: 3,
            border: '1px solid #e9eef5',
            boxShadow: '0 8px 22px rgba(15, 23, 42, 0.05)',
          }}
        >
          <TextField
            label="Mobile Number"
            size="small"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            error={!!error}
            helperText={error}
            fullWidth
            sx={{ maxWidth: { xs: '100%', sm: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              minWidth: { xs: '100%', sm: 130 },
              height: 40,
              bgcolor: '#003049',
              fontSize: 13,
              fontWeight: 800,
              borderRadius: 2,
              '&:hover': { bgcolor: '#002437' },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
          </Button>
        </Paper>

        {apiError && (
          <Paper
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: '#fff1f2',
              color: '#be123c',
              border: '1px solid #fecdd3',
              borderRadius: 2,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              {apiError}
            </Typography>
          </Paper>
        )}

        {customerData && (
          <>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {summaryCards.map((item, index) => (
                <Grid item xs={6} sm={6} md={3} key={index}>
                  <Card sx={cardSx}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Stack direction="row" spacing={1.4} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: item.bg,
                            color: item.color,
                            width: 40,
                            height: 40,
                          }}
                        >
                          {item.icon}
                        </Avatar>

                        <Box>
                          <Typography
                            sx={{
                              fontSize: { xs: 19, sm: 22 },
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            {item.value}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {item.label}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={7}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 1.7, sm: 2.2 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 40, height: 40 }}>
                        <Person fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography sx={sectionTitleSx}>Customer Details</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          Basic profile and first outlet information
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 1.8 }} />

                    <Grid container spacing={1.7}>
                      <Grid item xs={12} sm={6}>
                        <InfoItem label="Name" value={customerData?.customerDetails?.name} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <InfoItem label="Mobile" value={customerData?.customerDetails?.mobile} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <InfoItem label="Email" value={customerData?.customerDetails?.email} />
                      </Grid>

                      <Grid item xs={6} sm={3}>
                        <InfoItem
                          label="Joining Date"
                          value={formatDate(customerData?.customerDetails?.joiningDate)}
                        />
                      </Grid>

                      <Grid item xs={6} sm={3}>
                        <InfoItem label="Birth Date" value={birthdayCouponStatus.birthDateText} />
                      </Grid>

                      <Grid item xs={12}>
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: '#f8fafc',
                            borderRadius: 2,
                            border: '1px dashed #dbe4ef',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Storefront fontSize="small" color="primary" />
                            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                              First Ordered Outlet
                            </Typography>
                          </Stack>

                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            {customerData?.customerDetails?.firstOrderedOutlet?.name || '-'}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {customerData?.customerDetails?.firstOrderedOutlet?.address || '-'}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {customerData?.customerDetails?.firstOrderedOutlet?.city || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 1.7, sm: 2.2 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ bgcolor: '#e8f5e9', color: '#388e3c', width: 40, height: 40 }}>
                        <FactCheck fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography sx={sectionTitleSx}>Quick Facts</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          Support-friendly customer summary
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 1.8 }} />

                    <Stack spacing={1.2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                          Deleted Accounts
                        </Typography>
                        <Chip
                          size="small"
                          label={customerData?.deletedAccounts?.length || 0}
                          color={customerData?.deletedAccounts?.length > 0 ? 'error' : 'success'}
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                          Joining Offer
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            customerData?.couponDetails?.joiningOfferUsed
                              ? 'Used'
                              : joiningCouponExpired
                              ? 'Expired'
                              : 'Available'
                          }
                          color={
                            customerData?.couponDetails?.joiningOfferUsed
                              ? 'success'
                              : joiningCouponExpired
                              ? 'error'
                              : 'warning'
                          }
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                          Birthday Coupon
                        </Typography>
                        <Chip
                          size="small"
                          label={birthdayCouponStatus.label}
                          color={birthdayCouponStatus.color}
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                          Referral Coupon Used
                        </Typography>
                        <Chip
                          size="small"
                          label={customerData?.couponDetails?.referralCouponUsage ? 'Yes' : 'No'}
                          color={customerData?.couponDetails?.referralCouponUsage ? 'success' : 'default'}
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {cartData && cartItems.length > 0 && (
                <Grid item xs={12} lg={5}>
                  <Card sx={cardSx}>
                    <CardContent sx={{ p: { xs: 1.7, sm: 2.2 } }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: '#fff3e0', color: '#f57c00', width: 40, height: 40 }}>
                          <ShoppingBag fontSize="small" />
                        </Avatar>

                        <Box>
                          <Typography sx={sectionTitleSx}>Active Cart</Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            Current cart, coupon, freebie and addon details
                          </Typography>
                        </Box>
                      </Stack>

                      <Divider sx={{ my: 1.8 }} />

                      {cartCoupon && (
                        <Paper
                          sx={{
                            p: 1.5,
                            mb: 1.8,
                            borderRadius: 2,
                            bgcolor: cartCoupon?.isFreebieCoupon ? '#f0fdf4' : '#fffbeb',
                            border: '1px solid #e5e7eb',
                          }}
                        >
                          <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                            {cartCoupon?.couponCode || '-'}
                          </Typography>

                          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.3 }}>
                            {cartCoupon?.title || '-'} - {cartCoupon?.description || '-'}
                          </Typography>

                          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            {cartCoupon?.isFreebieCoupon && (
                              <Chip size="small" label="Freebie" color="success" />
                            )}
                            {cartCoupon?.isBogoCoupon && (
                              <Chip size="small" label="BOGO" color="warning" />
                            )}
                            {cartCoupon?.isCashbackCoupon && (
                              <Chip size="small" label="Cashback" color="info" />
                            )}
                            <Chip size="small" label={cartCoupon?.usageType || '-'} variant="outlined" />
                            <Chip
                              size="small"
                              label={cartCoupon?.status || '-'}
                              color={cartCoupon?.status === 'ACTIVE' ? 'success' : 'default'}
                            />
                          </Stack>
                        </Paper>
                      )}

                      <Grid container spacing={1}>
                        {[
                          ['Order Type', cartData?.ordersType || '-'],
                          ['Taxable', `₹${cartData?.totalTaxable || 0}`],
                          ['Tax', `₹${cartData?.totalTaxAmount || 0}`],
                          ['Discount', `₹${cartData?.totalDiscount || 0}`],
                          ['Delivery', `₹${cartData?.deliveryCharges || 0}`],
                          ['Grand Total', `₹${cartData?.grandTotal || 0}`],
                        ].map(([label, value]) => (
                          <Grid item xs={6} sm={4} key={label}>
                            <Paper
                              sx={{
                                p: 1.2,
                                borderRadius: 2,
                                bgcolor: label === 'Grand Total' ? '#ecfdf5' : '#f8fafc',
                                border: '1px solid #edf0f2',
                              }}
                            >
                              <Typography sx={labelSx}>{label}</Typography>
                              <Typography
                                sx={{
                                  ...valueSx,
                                  color: label === 'Grand Total' ? '#15803d' : '#111827',
                                }}
                              >
                                {value}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>

                      <Stack spacing={1.3} sx={{ mt: 1.8 }}>
                        {cartItems.map((item) => {
                          const freebie = isFreebieItem(item);
                          const addonTotal = getAddonTotal(item?.addons);

                          return (
                            <Paper
                              key={item._id}
                              sx={{
                                p: 1.4,
                                borderRadius: 2,
                                border: freebie ? '1px solid #86efac' : '1px solid #edf0f2',
                                bgcolor: freebie ? '#f0fdf4' : '#fff',
                              }}
                            >
                              <Stack direction="row" spacing={1.3} alignItems="flex-start">
                                <Box
                                  component="img"
                                  src={item?.product?.displayImageUrl}
                                  alt={item?.product?.name}
                                  sx={{
                                    width: 62,
                                    height: 62,
                                    borderRadius: 2,
                                    objectFit: 'cover',
                                    border: '1px solid #eee',
                                    flexShrink: 0,
                                  }}
                                />

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                                    <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                                      {item?.product?.name || '-'}
                                    </Typography>
                                    {freebie && <Chip size="small" label="Freebie" color="success" />}
                                  </Stack>

                                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {item?.product?.categoryData?.name || '-'}
                                  </Typography>

                                  <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 0.8 }}>
                                    <Chip size="small" label={`Qty: ${item?.quantity || 0}`} />
                                    <Chip size="small" label={`Rate: ₹${item?.rate || 0}`} />
                                    <Chip size="small" label={`Addon: ₹${addonTotal}`} />
                                    <Chip size="small" label={`Tax: ₹${item?.taxAmount || 0}`} />
                                    <Chip
                                      size="small"
                                      color={freebie ? 'success' : 'primary'}
                                      label={`Total: ₹${item?.grandTotal || 0}`}
                                    />
                                  </Stack>

                                  {item?.addons?.length > 0 && (
                                    <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                      {item.addons.map((addon, index) => (
                                        <Chip
                                          key={`${addon?.selectedValueId}-${index}`}
                                          size="small"
                                          variant="outlined"
                                          label={`${addon?.selectedValue || '-'} ₹${addon?.price || 0}`}
                                          color={addon?.selectedValueStatus === 'ACTIVE' ? 'default' : 'error'}
                                        />
                                      ))}
                                    </Stack>
                                  )}
                                </Box>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              <Grid item xs={12} lg={cartData && cartItems.length > 0 ? 7 : 12}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 1.7, sm: 2.2 } }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      spacing={1.5}
                      sx={{ mb: 1.8 }}
                    >
                      <Box>
                        <Typography sx={sectionTitleSx}>Recent Orders</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          Showing latest 15 orders based on selected filter
                        </Typography>
                      </Box>

                      <TextField
                        select
                        size="small"
                        label="Filter Orders"
                        value={orderFilter}
                        onChange={(e) => setOrderFilter(e.target.value)}
                        sx={{ minWidth: { xs: '100%', sm: 190 } }}
                      >
                        <MenuItem value="ALL">All</MenuItem>
                        <MenuItem value="COMPLETED">Completed</MenuItem>
                        <MenuItem value="DELIVERED">Delivered</MenuItem>
                        <MenuItem value="CANCELLED">Cancelled</MenuItem>
                        <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                        <MenuItem value="READY-TO-PICK">Ready To Pick</MenuItem>
                      </TextField>
                    </Stack>

                    <Divider sx={{ mb: 1.8 }} />

                    <Stack spacing={1.2}>
                      {recentOrders.length > 0 ? (
                        recentOrders.reverse().map((order) => {
                          const refund = order?.orderCancellationRequestInfo?.refundResponse || null;

                          return (
                            <Paper
                              key={order.orderId}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px solid #edf0f2',
                                bgcolor: '#fff',
                              }}
                            >
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.2}
                                justifyContent="space-between"
                              >
                                <Box>
                                  <Typography sx={labelSx}>Order</Typography>
                                  <Typography sx={valueSx}>
                                    #{order?.order_sequence}
                                    {order?.order_no}
                                  </Typography>
                                </Box>

                                <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                                  <Chip
                                    label={order?.status || '-'}
                                    color={getStatusColor(order?.status)}
                                    size="small"
                                  />
                                  <Chip
                                    label={order.paymentStatus?.status || 'N/A'}
                                    color={getPaymentChipColor(order.paymentStatus?.status)}
                                    size="small"
                                    icon={<Payments fontSize="small" />}
                                  />
                                </Stack>
                              </Stack>

                              <Grid container spacing={1.3} sx={{ mt: 0.5 }}>
                                <Grid item xs={12} sm={7}>
                                  <Typography sx={labelSx}>Outlet</Typography>
                                  <Typography sx={valueSx}>{order.outlet?.name || '-'}</Typography>
                                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {order.outlet?.city || '-'}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} sm={5}>
                                  <Typography sx={labelSx}>Created</Typography>
                                  <Typography sx={valueSx}>
                                    {formatDateTime(order.createdAt)}
                                  </Typography>
                                </Grid>
                              </Grid>

                              {refund && (
                                <Paper
                                  sx={{
                                    p: 1.2,
                                    bgcolor: '#fffbeb',
                                    borderRadius: 2,
                                    mt: 1.2,
                                    border: '1px solid #fde68a',
                                  }}
                                >
                                  <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
                                    Refund Details
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    ID: {refund.refund_id || refund.id || '-'} | Status:{' '}
                                    {refund.status || '-'} | Amount: ₹
                                    {refund.amount ? refund.amount / 100 : 0}
                                  </Typography>
                                </Paper>
                              )}
                            </Paper>
                          );
                        })
                      ) : (
                        <EmptyText>No orders found.</EmptyText>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 1.7, sm: 2.2 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ bgcolor: '#e8f5e9', color: '#388e3c', width: 40, height: 40 }}>
                        <Group fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography sx={sectionTitleSx}>Referred Users</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          Users referred by this customer
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 1.8 }} />

                    <Stack spacing={1.2}>
                      {referrals.length > 0 ? (
                        referrals.map((user, index) => (
                          <Paper
                            key={`${user.email}-${index}`}
                            sx={{
                              p: 1.4,
                              borderRadius: 2,
                              border: '1px solid #edf0f2',
                            }}
                          >
                            <Stack direction="row" spacing={1.2} alignItems="center">
                              <Avatar sx={{ width: 36, height: 36, fontSize: 14 }}>
                                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                              </Avatar>

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                                  {user.name || '-'}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: 'text.secondary',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {user.email || '-'}
                                </Typography>
                                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                                  Joined: {formatDate(user.joinedAt)}
                                </Typography>
                              </Box>

                              <Chip
                                size="small"
                                label={user.isFirstPurchase ? 'Purchased' : 'No Purchase'}
                                color={user.isFirstPurchase ? 'success' : 'default'}
                              />
                            </Stack>
                          </Paper>
                        ))
                      ) : (
                        <EmptyText>No referred users found.</EmptyText>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 1.7, sm: 2.2 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ bgcolor: '#fff3e0', color: '#f57c00', width: 40, height: 40 }}>
                        <Redeem fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography sx={sectionTitleSx}>Allocated Coupons</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          Coupons currently allocated to customer
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 1.8 }} />

                    <Stack spacing={1.2}>
                      {coupons.length > 0 ? (
                        coupons.map((coupon) => (
                          <Paper
                            key={coupon._id}
                            sx={{
                              p: 1.4,
                              borderRadius: 2,
                              border: '1px solid #edf0f2',
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              spacing={1}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                                  {coupon.title || '-'}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                  {coupon.description || '-'}
                                </Typography>
                              </Box>

                              <Chip
                                size="small"
                                label={coupon.status || '-'}
                                color={coupon.status === 'ACTIVE' ? 'success' : 'default'}
                              />
                            </Stack>

                            <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 1.2 }}>
                              <Chip size="small" variant="outlined" label={`Code: ${coupon.couponCode || '-'}`} />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Discount: ${
                                  coupon.discountPercent
                                    ? `${coupon.discountPercent}%`
                                    : coupon.discountAmount
                                    ? `₹${coupon.discountAmount}`
                                    : '-'
                                }`}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Min: ₹${coupon.minOrderAmount || 0}`}
                              />
                            </Stack>

                            <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 1 }}>
                              <CalendarMonth fontSize="small" color="action" />
                              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                {formatDate(coupon.validityStartDate)} -{' '}
                                {formatDate(coupon.validityEndDate)}
                              </Typography>
                            </Stack>

                            {coupon.termsAndConditions?.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>
                                  Terms
                                </Typography>
                                {coupon.termsAndConditions.slice(0, 3).map((term, index) => (
                                  <Typography
                                    key={index}
                                    sx={{ fontSize: 11.5, color: 'text.secondary' }}
                                  >
                                    • {term}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Paper>
                        ))
                      ) : (
                        <EmptyText>No coupons allocated.</EmptyText>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CustomerSupport;